import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Loader2, MapPin, Search } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { CITIES, DOTS, MAP, OUTLINE } from "./ukmap";
import { Eyebrow, MaskLines, Reveal, ease, useIsMobile } from "./motion";

const LINKS = [
  ["London", "Birmingham"], ["Birmingham", "Manchester"], ["Manchester", "Leeds"], ["Leeds", "Newcastle"], ["Newcastle", "Edinburgh"],
  ["Edinburgh", "Glasgow"], ["Edinburgh", "Aberdeen"], ["Aberdeen", "Inverness"], ["Glasgow", "Belfast"], ["Manchester", "Liverpool"],
  ["Birmingham", "Nottingham"], ["London", "Norwich"], ["London", "Southampton"], ["London", "Bristol"], ["Bristol", "Cardiff"], ["Bristol", "Plymouth"],
];
const LABELS = ["London", "Manchester", "Glasgow", "Edinburgh", "Belfast", "Cardiff", "Newcastle", "Birmingham", "Plymouth", "Norwich", "Inverness"];
const FULL = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;
const OUT = /^[A-Z]{1,2}\d[A-Z\d]?$/;
const proj = (lon, lat) => [(lon - MAP.lon0) * MAP.c * MAP.k, (MAP.lat0 - lat) * MAP.k];
const seg = ([a, b]) => `M${CITIES[a][0]},${CITIES[a][1]} L${CITIES[b][0]},${CITIES[b][1]}`;

const lookup = async (code) => {
  const full = FULL.test(code);
  const res = await fetch(`https://api.postcodes.io/${full ? "postcodes" : "outcodes"}/${encodeURIComponent(code)}`);
  const j = await res.json();
  if (j.status !== 200 || !j.result?.latitude) return null;
  const d = j.result;
  return full
    ? { code: d.postcode, lat: d.latitude, lon: d.longitude, place: d.admin_district || d.parish, region: d.region || d.country }
    : { code: d.outcode, lat: d.latitude, lon: d.longitude, place: d.admin_district?.[0], region: d.country?.[0] };
};

const UkMap = ({ pin }) => {
  const p = pin?.lat ? proj(pin.lon, pin.lat) : null;
  return (
    <svg viewBox="15 2 362 666" className="w-full max-w-[330px] sm:max-w-none max-h-[70svh] sm:max-h-[700px]" data-testid="uk-map" role="img" aria-label="Map of the UK — all regions covered">
      <defs>
        <g id="ukdots">{DOTS.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="1.7" />)}</g>
        <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id="band"><rect x="0" width="420" height="140" fill="url(#bandGrad)"><animate attributeName="y" values="40;700" dur="5s" repeatCount="indefinite" /></rect></mask>
      </defs>
      <path d={OUTLINE} fill="rgba(0,240,255,0.03)" stroke="rgba(0,240,255,0.35)" strokeWidth="0.8" style={{ filter: "drop-shadow(0 0 6px rgba(0,240,255,0.5))" }} />
      <use href="#ukdots" fill="rgba(148,163,184,0.35)" />
      <use href="#ukdots" fill="#00F0FF" mask="url(#band)" style={{ filter: "drop-shadow(0 0 3px #00F0FF)" }} />
      {LINKS.map((l, i) => (
        <g key={i}>
          <motion.path d={seg(l)} fill="none" stroke="#00F0FF" strokeOpacity="0.45" strokeWidth="0.9" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.2 + i * 0.06, ease }} />
          <circle r="1.8" fill="#10B981" style={{ filter: "drop-shadow(0 0 4px #10B981)" }}>
            <animateMotion dur={`${2 + (i % 4) * 0.5}s`} repeatCount="indefinite" path={seg(i % 2 ? [l[1], l[0]] : l)} />
          </circle>
        </g>
      ))}
      {Object.entries(CITIES).map(([n, [x, y]]) => (
        <g key={n}>
          <circle cx={x} cy={y} r="6" fill="none" stroke="#00F0FF" className="topo-ping" />
          <circle cx={x} cy={y} r="2.6" fill="#E6FDFF" style={{ filter: "drop-shadow(0 0 5px #00F0FF)" }} />
          {LABELS.includes(n) && <text x={x + 7} y={y + 3} fill="rgba(226,232,240,0.75)" fontSize="7.5" fontFamily="JetBrains Mono, monospace" letterSpacing="1">{n.toUpperCase()}</text>}
        </g>
      ))}
      <AnimatePresence>
        {p && (
          <motion.g key={pin.code} initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.7, ease }} data-testid="uk-map-pin">
            <circle cx={p[0]} cy={p[1]} r="10" fill="none" stroke="#F59E0B" className="topo-ping" />
            <circle cx={p[0]} cy={p[1]} r="4" fill="#F59E0B" style={{ filter: "drop-shadow(0 0 8px #F59E0B)" }} />
            <text x={p[0]} y={p[1] - 12} textAnchor="middle" fill="#FCD34D" fontSize="9" fontWeight="700" fontFamily="JetBrains Mono, monospace">{pin.code}</text>
          </motion.g>
        )}
      </AnimatePresence>
    </svg>
  );
};

const Result = ({ res }) => {
  const book = () => {
    window.dispatchEvent(new CustomEvent("lns:prefill", { detail: `Postcode: ${res.code}${res.place ? ` (${res.place})` : ""}\nI'd like a free consultation.` }));
    scrollToId("contact");
  };
  return (
    <motion.div key={res.code + res.msg} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.45, ease }} className={`mt-6 border p-5 ${res.ok ? "border-emerald-400/40 bg-emerald-400/5" : "border-rose-400/40 bg-rose-400/5"}`} data-testid="postcode-result">
      <p className={`font-display text-xl font-bold ${res.ok ? "text-emerald-400" : "text-rose-300"}`}>{res.ok ? "Yes — we cover you." : "Hmm, check that postcode."}</p>
      <p className="mt-1 text-slate-300" data-testid="postcode-result-text">{res.msg || `${res.code} · ${res.place}${res.region ? `, ${res.region}` : ""}. On-site installs and remote support available.`}</p>
      {res.ok && (
        <button type="button" onClick={book} data-testid="postcode-book" className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-300 hover:text-white transition-colors">
          Book a free consultation <ArrowUpRight size={14} />
        </button>
      )}
    </motion.div>
  );
};

export const ServiceArea = () => {
  const [pc, setPc] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const mobile = useIsMobile();

  const check = async (e) => {
    e.preventDefault();
    const code = pc.replace(/\s+/g, "").toUpperCase();
    if (/^(GY|JE|IM)\d/.test(code)) return setRes({ ok: true, code, msg: "Channel Islands & Isle of Man — yes, just get in touch and we'll plan the visit." });
    if (!FULL.test(code) && !OUT.test(code)) return setRes({ ok: false, code, msg: "That doesn't look like a UK postcode — try something like M1 1AE or SW1A." });
    setLoading(true);
    try {
      const hit = await lookup(code);
      setRes(hit ? { ok: true, ...hit } : { ok: true, code, msg: `We couldn't pinpoint ${code}, but we cover the whole of the UK.` });
    } catch {
      setRes({ ok: true, code, msg: `Yes — ${code} is covered. We work across the whole of the UK.` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="coverage" className="relative py-24 md:py-32 overflow-hidden" data-testid="service-area-section">
      <div className="absolute right-0 top-1/3 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[140px]" aria-hidden />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 min-w-0">
          <Eyebrow>Service area · all of the UK</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1]">
            <MaskLines lines={["Every postcode.", <span className="font-serif-i font-normal text-cyan-300">Nationwide.</span>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-lg text-slate-400 leading-relaxed">From Cornwall to the Highlands, Belfast to Norwich — on-site installs and remote support across England, Scotland, Wales and Northern Ireland.</p>
            <form onSubmit={check} className="mt-10 flex w-full max-w-lg items-center gap-1.5 sm:gap-2 rounded-full border border-white/15 bg-[#0B0F17] p-1.5 focus-within:border-cyan-300 transition-colors" data-testid="postcode-form">
              <MapPin size={18} className="ml-3 sm:ml-4 text-cyan-300 shrink-0" />
              <input value={pc} onChange={(e) => setPc(e.target.value)} placeholder={mobile ? "Your postcode" : "Do you cover my postcode?"} aria-label="Postcode" maxLength={9} className="flex-1 min-w-0 bg-transparent px-1.5 sm:px-2 py-3 text-base sm:text-lg uppercase outline-none placeholder:normal-case placeholder:text-slate-600" data-testid="postcode-input" />
              <button type="submit" disabled={!pc.trim() || loading} data-testid="postcode-submit" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-cyan-300 px-4 sm:px-6 py-3 font-semibold text-void hover:bg-white transition-colors disabled:opacity-50">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Check
              </button>
            </form>
            <AnimatePresence mode="wait">{res && <Result res={res} />}</AnimatePresence>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-lg">
              {["England", "Scotland", "Wales", "N. Ireland"].map((n) => (
                <div key={n} className="bg-void px-3 py-4 text-center">
                  <p className="font-mono text-[9px] tracking-[0.18em] text-emerald-400">● COVERED</p>
                  <p className="mt-1 text-sm font-semibold">{n}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-6 min-w-0 flex justify-center" delay={0.15}>
          <UkMap pin={res?.ok ? res : null} />
        </Reveal>
      </div>
    </section>
  );
};
